import React, { useState } from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import { PopoverMenu, PopoverMenuItem } from "../../common/general/PopoverMenu/PopoverMenu";
import CustomText from "../../common/general/CustomText/CustomText";
import { LucideExpand } from "lucide-react";
import {
  GradebookActivityType,
  GradebookCellStatus,
  StudentActivityStatus,
  type GradebookActivityColumn,
  type GradebookCell as GeneratedGradebookCell,
  type GradebookStudentRow,
} from "@axio/graphql/generated/graphql";

const ExpandIcon = LucideExpand as unknown as React.ComponentType<{ size?: number; color?: string; style?: object }>;

export const EXCUSED_STATUS = "EXCUSED" as const;
export type CellStatus = GradebookCellStatus | typeof EXCUSED_STATUS | null;

export type GradeCell = Omit<GeneratedGradebookCell, "status"> & {
  status?: CellStatus;
  isMissing?: boolean;
};

export type GradebookStudent = Omit<GradebookStudentRow, "cells"> & {
  grades: Record<string, GradeCell>;
};

export function getGradeColor(score: number): string {
  if (score >= 90) return "#3B7C0F";
  if (score >= 80) return "#101828";
  if (score >= 70) return "#B54708";
  if (score >= 60) return "#B91C1C";
  return "#DC2626";
}

interface GradebookCellProps {
  student: GradebookStudent;
  assignment: GradebookActivityColumn;
  cell: GradeCell;
  rowEven: boolean;
  onOpenSheet?: (studentId: string, activityId: string, activityType: GradebookActivityType) => void;
  onCellStatusChange?: (studentId: string, assignmentId: string, status: StudentActivityStatus) => void;
}

export function GradebookCell({
  student,
  assignment,
  cell,
  rowEven,
  onOpenSheet,
  onCellStatusChange,
}: GradebookCellProps) {
  const [isHovered, setIsHovered] = useState(false);

  const studentId = student.student?.id ?? "";
  const studentName = student.student?.name ?? "";
  const activityId = assignment.activityId;

  const isMissing = cell?.status === GradebookCellStatus.Missing || !!cell?.isMissing;
  const isExcused = cell?.status === EXCUSED_STATUS;
  const needsGrading =
    cell?.status === GradebookCellStatus.NeedsGrading ||
    cell?.status === GradebookCellStatus.NeedsReview ||
    cell?.status === GradebookCellStatus.RetakeRequested;
  const isLate = !!cell?.isLate || cell?.status === GradebookCellStatus.Late;
  const isRetake = !!cell?.isRetake;
  const hasSubmission = !!cell?.submissionId;
  const hasStatus = isMissing || isExcused;

  const activityLabel =
    assignment.activityType === GradebookActivityType.Assignment
      ? "Assignment"
      : assignment.activityType === GradebookActivityType.Assessment
        ? "Assessment"
        : "Project";

  let bgColor = rowEven ? "#FFFFFF" : "#F9FAFB";
  if (isMissing) bgColor = "#FEF2F2";
  else if (needsGrading) bgColor = "#FFFBEB";

  const menuItems: PopoverMenuItem[] = [];
  if (onCellStatusChange) {
    if (!isExcused)
      menuItems.push({
        key: StudentActivityStatus.Excused,
        label: `Excuse ${activityLabel}`,
        onPress: () => onCellStatusChange(studentId, activityId, StudentActivityStatus.Excused),
      });
    if (!isMissing)
      menuItems.push({
        key: StudentActivityStatus.Missing,
        label: "Mark as Missing",
        onPress: () => onCellStatusChange(studentId, activityId, StudentActivityStatus.Missing),
      });
    if (!isLate)
      menuItems.push({
        key: StudentActivityStatus.LateAllowed,
        label: "Allow Late Submission",
        onPress: () => onCellStatusChange(studentId, activityId, StudentActivityStatus.LateAllowed),
      });
    if (hasStatus)
      menuItems.push({
        key: StudentActivityStatus.Cleared,
        label: "Clear Status",
        danger: true,
        onPress: () => onCellStatusChange(studentId, activityId, StudentActivityStatus.Cleared),
      });
  }

  return (
    <View
      style={[
        styles.cell,
        { backgroundColor: bgColor },
        isLate ? styles.lateBorder : undefined,
        isHovered ? styles.cellHovered : undefined,
      ]}
      // @ts-expect-error - web only
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <View style={styles.pressableFill}>
        <View style={styles.badgeRow}>
          {isExcused ? (
            <CustomText style={styles.excusedText} text="Excused" size="xs" useTranslationText={false} />
          ) : isMissing ? (
            <CustomText style={styles.missingText} text="Missing" size="xs" useTranslationText={false} />
          ) : cell?.percentageScore != null ? (
            <CustomText
              style={[styles.scoreText, { color: getGradeColor(cell.percentageScore) }]}
              text={`${cell.percentageScore}`}
              size="xs"
              useTranslationText={false}
            />
          ) : (
            <CustomText style={styles.emptyText} text="–" size="xs" useTranslationText={false} />
          )}

          {isLate && !isMissing && (
            <View style={styles.lateBadge}>
              <CustomText style={styles.lateBadgeText} text="Late" size="xs" useTranslationText={false} />
            </View>
          )}
          {isRetake && (
            <View style={styles.retakeBadge}>
              <CustomText style={styles.retakeBadgeText} text="Retake" size="xs" useTranslationText={false} />
            </View>
          )}
          {needsGrading && hasSubmission && (
            <View style={styles.gradeBadge}>
              <CustomText style={styles.gradeBadgeText} text="Grade" size="xs" useTranslationText={false} />
            </View>
          )}
        </View>
      </View>

      {/* Actions */}
      {(onOpenSheet || menuItems.length > 0) && (
        <View
          style={[
            styles.actions,
            {
              opacity: isHovered ? 1 : 0,
              pointerEvents: isHovered ? "auto" : "none",
            },
          ]}>
          {onOpenSheet && !isExcused && (
            <TouchableOpacity
              style={styles.actionBtn}
              onPress={() => onOpenSheet(studentId, activityId, assignment.activityType)}
              accessibilityLabel={`Open ${assignment.activityType.toLowerCase()} for ${studentName}`}>
              <ExpandIcon size={12} color="#6B7280" style={{ pointerEvents: "none" }} />
            </TouchableOpacity>
          )}
          {menuItems.length > 0 && (
            <PopoverMenu
              items={menuItems}
              triggerAccessibilityLabel={`More actions for ${studentName}`}
              triggerStyle={styles.actionBtn}
              renderTrigger={() => (
                <CustomText style={styles.actionIcon} text="•••" size="xs" useTranslationText={false} />
              )}
            />
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  cell: {
    width: 144,
    height: 44,
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    position: "relative",
    overflow: "visible",
  },
  cellHovered: {
    backgroundColor: "#EFF6FF",
  },
  lateBorder: {
    borderLeftWidth: 2,
    borderLeftColor: "#F59E0B",
  },
  // Fills the full cell
  pressableFill: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    flexShrink: 1,
  },
  scoreText: {
    fontSize: 13,
    fontWeight: "600",
    fontVariant: ["tabular-nums"],
  },
  excusedText: {
    fontSize: 11,
    color: "#6B7280",
    fontStyle: "italic",
  },
  emptyText: {
    fontSize: 14,
    color: "#9CA3AF",
  },
  missingText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#DC2626",
  },
  lateBadge: {
    backgroundColor: "#FEF3C7",
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  lateBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    color: "#B45309",
  },
  retakeBadge: {
    backgroundColor: "#EDE9FE",
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  retakeBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    color: "#7C3AED",
  },
  gradeBadge: {
    backgroundColor: "#FEF3C7",
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  gradeBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    color: "#B45309",
  },
  // Absolutely positioned over the cell, rendered after the Pressable in DOM order
  // so it sits on top — clicks here never reach the Pressable beneath it
  actions: {
    position: "absolute",
    right: 4,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
    zIndex: 1,
  },
  actionBtn: {
    paddingHorizontal: 4,
    alignSelf: "stretch",
    justifyContent: "center",
    borderRadius: 4,
  },
  actionIcon: {
    fontSize: 12,
    color: "#6B7280",
  },
});
